import React, { useEffect, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import { withStations } from 'components/queries/GetStationsQuery';
import { historyShape, stationsShape, userShape } from 'constants/propTypes';
import PropTypes from 'prop-types';
import Button from 'components/ui/Button';
import { TabContainer, StyledTabButton } from 'components/ui/Tabs/Tabs.styles';
import { CHEAPEST_FARES_SEARCH } from 'store/queries';
import { isUserEurostarAgent } from 'utils/user';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { STANDARD, PLUS, PREMIER } from 'constants/classOfService';
import { PAX_DESCRIPTIONS } from 'constants/booking';
import FareCalendar from './FareCalendar';
import MonthSelect from './MonthSelect';
import { Page, Fields, RouteFields, SearchAction, Calendars, PassengerSection, PassengerPanel } from './LowestFareFinder.styles';
import {
  canUseLowestFareFinder,
  getDirectStations,
  getDefaultSearch,
  isValidSearch,
  buildCalendarRequest,
  buildJourneySearchUrl
} from './LowestFareFinder.utils';

export const LowestFareFinder = ({ user, stations, history, intl }) => {
  const client = useApolloClient();
  const directStations = getDirectStations(stations);
  const [form, setForm] = useState(() => getDefaultSearch(user, directStations));
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestId = useRef(0);
  const allowed = canUseLowestFareFinder(user);

  const search = async (values) => {
    if (!isValidSearch(values) || !allowed) return;
    const id = ++requestId.current;
    setSelected({});
    setResults(null);
    setLoading(true);
    setError(false);
    try {
      const directions = values.journeyType === 'return' ? ['outbound', 'inbound'] : ['outbound'];
      const responses = await Promise.all(
        directions.map((direction) =>
          client.query({
            query: CHEAPEST_FARES_SEARCH,
            variables: buildCalendarRequest(values, direction),
            fetchPolicy: 'network-only'
          })
        )
      );
      if (id === requestId.current)
        setResults({
          form: values,
          outbound: responses[0].data.cheapestFaresSearch[0]?.cheapestFares || [],
          inbound: responses[1]?.data.cheapestFaresSearch[0]?.cheapestFares || []
        });
    } catch (err) {
      if (id === requestId.current) setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    search(form);
    return () => {
      requestId.current += 1;
    };
    // The initial search uses the user's defaults only once on entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (key, value) => {
    requestId.current += 1;
    setLoading(false);
    setSelected({});
    setResults(null);
    setForm((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'accessible' && value && { fareType: productFamilyCodes.PUB }),
      ...(key === 'origin' && value === previous.destination && { destination: '' }),
      ...(key === 'outboundMonth' && value > previous.inboundMonth && { inboundMonth: value })
    }));
  };
  const label = (id) => intl.formatMessage({ id });
  const field = (key, title, options, disabled = false) => (
    <label>
      {label(title)}
      <select value={form[key]} onChange={(event) => update(key, event.target.value)} disabled={disabled}>
        {options.map(({ value, text }) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
  const stationOptions = [
    { value: '', text: label('lowestFareFinder.chooseStation') },
    ...directStations.map(({ uic, name }) => ({ value: uic, text: name }))
  ];
  const classOptions = [STANDARD, PLUS, PREMIER].map((value) => ({ value, text: label(`lowestFareFinder.class.${value}`) }));
  const stationName = (uic) => directStations.find((station) => station.uic === uic)?.name;
  const canGetTimes = !!results && !!selected.outbound && (results.form.journeyType === 'single' || !!selected.inbound);

  if (!allowed) return <Redirect to="/" />;

  return (
    <Page>
      <h1>
        <FormattedMessage id="lowestFareFinder.title" />
      </h1>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          search(form);
        }}
      >
        <PassengerSection aria-label={label('lowestFareFinder.bookingType')}>
          <TabContainer
            count={user.canHaveAccessibleTravelBookings ? 2 : 1}
            role="tablist"
            aria-label={label('lowestFareFinder.bookingType')}
          >
            {[false, true]
              .filter((accessible) => !accessible || user.canHaveAccessibleTravelBookings)
              .map((accessible) => (
                <StyledTabButton
                  key={String(accessible)}
                  id={`passenger-tab-${accessible ? 'accessible' : 'general'}`}
                  type="button"
                  role="tab"
                  selected={form.accessible === accessible}
                  tabIndex={form.accessible === accessible ? 0 : -1}
                  aria-controls="passenger-panel"
                  aria-selected={form.accessible === accessible}
                  onClick={() => update('accessible', accessible)}
                  onKeyDown={(event) => {
                    if (!user.canHaveAccessibleTravelBookings) return;
                    const next = { ArrowLeft: !accessible, ArrowRight: !accessible, Home: false, End: true }[event.key];
                    if (next === undefined) return;
                    event.preventDefault();
                    update('accessible', next);
                    document.getElementById(`passenger-tab-${next ? 'accessible' : 'general'}`).focus();
                  }}
                >
                  <FormattedMessage id={accessible ? 'bookingType.disability' : 'lowestFareFinder.general'} />
                </StyledTabButton>
              ))}
          </TabContainer>
          <PassengerPanel
            id="passenger-panel"
            role="tabpanel"
            aria-labelledby={`passenger-tab-${form.accessible ? 'accessible' : 'general'}`}
          >
            <Fields $columns={form.accessible ? 2 : 4}>
              {form.accessible ? (
                <>
                  {field(
                    'wheelchairType',
                    'lowestFareFinder.wheelchairUser',
                    ['adult', 'child'].map((value) => ({ value, text: label(`lowestFareFinder.wheelchair.${value}`) }))
                  )}
                  <label>
                    <FormattedMessage id="lowestFareFinder.companion" />
                    <input type="checkbox" checked={form.companion} onChange={(event) => update('companion', event.target.checked)} />
                  </label>
                </>
              ) : (
                ['adults', 'seniors', 'children', 'youths'].map((key) => (
                  <label key={key}>
                    <span>
                      {label(`lowestFareFinder.${key}`)}{' '}
                      <span id={`passenger-age-${key}`} style={{ color: '#666' }}>
                        ({label(`groupBooking.${PAX_DESCRIPTIONS[key]}`)})
                      </span>
                    </span>
                    <input
                      aria-label={label(`lowestFareFinder.${key}`)}
                      aria-describedby={`passenger-age-${key}`}
                      type="number"
                      min="0"
                      max="9"
                      required
                      value={form[key]}
                      onChange={(event) => update(key, Math.max(0, Math.min(9, Math.floor(Number(event.target.value)))))}
                    />
                  </label>
                ))
              )}
            </Fields>
          </PassengerPanel>
        </PassengerSection>
        <RouteFields>
          {field(
            'journeyType',
            'lowestFareFinder.journeyType',
            ['return', 'single'].map((value) => ({ value, text: label(`lowestFareFinder.${value}`) }))
          )}
          {field('origin', 'lowestFareFinder.from', stationOptions)}
          {field(
            'destination',
            'lowestFareFinder.to',
            stationOptions.filter(({ value }) => value !== form.origin)
          )}
        </RouteFields>
        <Fields>
          {['outbound', ...(form.journeyType === 'return' ? ['inbound'] : [])].map((direction) => (
            <React.Fragment key={direction}>
              <MonthSelect
                label={label(`lowestFareFinder.${direction}`)}
                min={direction === 'inbound' ? form.outboundMonth : moment().format('YYYY-MM')}
                value={form[`${direction}Month`]}
                onChange={(value) => update(`${direction}Month`, value)}
              />
              {field(`${direction}Class`, 'lowestFareFinder.class', classOptions, form.accessible)}
            </React.Fragment>
          ))}
        </Fields>
        <Fields>
          {field(
            'currency',
            'lowestFareFinder.currency',
            (user.paymentCurrencies || []).map((value) => ({ value, text: value }))
          )}
          {field(
            'fareType',
            'lowestFareFinder.fareType',
            [productFamilyCodes.PUB, ...(isUserEurostarAgent(user) ? [productFamilyCodes.RIT] : [])].map((value) => ({
              value,
              text: value
            })),
            form.accessible
          )}
          <SearchAction>
            <Button type="submit" disabled={!isValidSearch(form) || loading} loading={loading}>
              <FormattedMessage id="lowestFareFinder.find" />
            </Button>
          </SearchAction>
        </Fields>
      </form>
      {error && (
        <p role="alert">
          <FormattedMessage id="lowestFareFinder.error" />
        </p>
      )}
      {loading && (
        <p role="status">
          <FormattedMessage id="generic.loading" />
        </p>
      )}
      {results && (
        <>
          <Calendars>
            {['outbound', ...(results.form.journeyType === 'return' ? ['inbound'] : [])].map((direction) => (
              <FareCalendar
                key={direction}
                direction={direction}
                month={results.form[`${direction}Month`]}
                fares={results[direction]}
                currency={results.form.currency}
                selected={selected[direction]}
                origin={stationName(direction === 'outbound' ? results.form.origin : results.form.destination)}
                destination={stationName(direction === 'outbound' ? results.form.destination : results.form.origin)}
                minDate={direction === 'inbound' ? selected.outbound?.date : undefined}
                maxDate={direction === 'outbound' ? selected.inbound?.date : undefined}
                onSelect={(fare) => setSelected((previous) => ({ ...previous, [direction]: fare }))}
              />
            ))}
          </Calendars>
          <Button disabled={!canGetTimes} onClick={() => history.push(buildJourneySearchUrl(results.form, selected, directStations))}>
            <FormattedMessage id="lowestFareFinder.getTrainTimes" />
          </Button>
        </>
      )}
    </Page>
  );
};

LowestFareFinder.propTypes = {
  user: userShape.isRequired,
  stations: stationsShape.isRequired,
  history: historyShape.isRequired,
  intl: PropTypes.object.isRequired
};

export default withStations(injectIntl(LowestFareFinder));
